import { Alarm, AlarmFilters, AlarmsResponse } from './types';

// Mock data for alarms
const mockAlarms: Alarm[] = [
  {
    id: '1',
    deviceId: 'AHU-01',
    deviceName: 'AHU Floor 1',
    timestamp: new Date(Date.now() - 30 * 60000).toISOString(), // 30 minutes ago
    type: 'temperature',
    severity: 'critical',
    status: 'active',
    message: 'High temperature detected',
    code: 'A103',
    details: 'Temperature exceeded operating threshold of 35°C. Current temperature: 38°C.'
  },
  {
    id: '2',
    deviceId: 'AHU-03',
    deviceName: 'AHU Floor 3',
    timestamp: new Date(Date.now() - 45 * 60000).toISOString(), // 45 minutes ago
    type: 'communication',
    severity: 'warning',
    status: 'active',
    message: 'Communication error',
    code: 'C201',
    details: 'Device failed to respond to status request after 3 attempts.'
  },
  {
    id: '3',
    deviceId: 'AHU-02',
    deviceName: 'AHU Floor 2',
    timestamp: new Date(Date.now() - 2 * 60 * 60000).toISOString(), // 2 hours ago
    type: 'power',
    severity: 'info',
    status: 'acknowledged',
    message: 'Power fluctuation detected',
    code: 'P301',
    details: 'Power supply fluctuated between 210V and 230V over a 5-minute period.',
    acknowledgedBy: 'Pamekitti',
    acknowledgedAt: new Date(Date.now() - 1.5 * 60 * 60000).toISOString() // 1.5 hours ago
  },
  {
    id: '4',
    deviceId: 'AHU-05',
    deviceName: 'AHU Floor 5',
    timestamp: new Date(Date.now() - 5 * 60 * 60000).toISOString(), // 5 hours ago
    type: 'sensor',
    severity: 'warning',
    status: 'resolved',
    message: 'Humidity sensor malfunction',
    code: 'S401',
    details: 'Humidity sensor reporting inconsistent values. Last reading: 95%.',
    acknowledgedBy: 'Jane Smith',
    acknowledgedAt: new Date(Date.now() - 4.5 * 60 * 60000).toISOString(), // 4.5 hours ago
    resolvedBy: 'Jane Smith',
    resolvedAt: new Date(Date.now() - 3 * 60 * 60000).toISOString() // 3 hours ago
  },
  {
    id: '5',
    deviceId: 'AHU-01',
    deviceName: 'AHU Floor 1',
    timestamp: new Date(Date.now() - 12 * 60 * 60000).toISOString(), // 12 hours ago
    type: 'system',
    severity: 'critical',
    status: 'resolved',
    message: 'System restart required',
    code: 'S501',
    details: 'System detected internal error and requires restart to recover.',
    acknowledgedBy: 'Pamekitti',
    acknowledgedAt: new Date(Date.now() - 11.5 * 60 * 60000).toISOString(), // 11.5 hours ago
    resolvedBy: 'Pamekitti',
    resolvedAt: new Date(Date.now() - 10 * 60 * 60000).toISOString() // 10 hours ago
  },
  {
    id: '6',
    deviceId: 'AHU-04',
    deviceName: 'AHU Floor 4',
    timestamp: new Date(Date.now() - 15 * 60000).toISOString(), // 15 minutes ago
    type: 'temperature',
    severity: 'warning',
    status: 'active',
    message: 'Temperature rising rapidly',
    code: 'A104',
    details: 'Temperature increased by 5°C in the last 10 minutes. Current: 32°C.'
  },
  {
    id: '7',
    deviceId: 'AHU-02',
    deviceName: 'AHU Floor 2',
    timestamp: new Date(Date.now() - 8 * 60 * 60000).toISOString(), // 8 hours ago
    type: 'communication',
    severity: 'info',
    status: 'acknowledged',
    message: 'Intermittent connection',
    code: 'C202',
    details: 'Device experiencing intermittent connection issues. 3 drops in the last hour.',
    acknowledgedBy: 'Jane Smith',
    acknowledgedAt: new Date(Date.now() - 7 * 60 * 60000).toISOString() // 7 hours ago
  }
];

/**
 * Fetch alarms with optional filters
 */
export async function getAlarms(filters?: AlarmFilters): Promise<AlarmsResponse> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Apply filters
  let filteredAlarms = [...mockAlarms];
  
  if (filters) {
    if (filters.severity) {
      filteredAlarms = filteredAlarms.filter(alarm => alarm.severity === filters.severity);
    }
    
    if (filters.status) {
      filteredAlarms = filteredAlarms.filter(alarm => alarm.status === filters.status);
    }
    
    if (filters.type) {
      filteredAlarms = filteredAlarms.filter(alarm => alarm.type === filters.type);
    }
    
    if (filters.deviceId) {
      filteredAlarms = filteredAlarms.filter(alarm => alarm.deviceId === filters.deviceId);
    }
    
    if (filters.startDate) {
      const startDate = new Date(filters.startDate).getTime();
      filteredAlarms = filteredAlarms.filter(alarm => new Date(alarm.timestamp).getTime() >= startDate);
    }
    
    if (filters.endDate) {
      const endDate = new Date(filters.endDate).getTime();
      filteredAlarms = filteredAlarms.filter(alarm => new Date(alarm.timestamp).getTime() <= endDate);
    }
  }
  
  // Sort by timestamp (newest first)
  filteredAlarms.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  
  return {
    success: true,
    status: 200,
    message: 'Alarms retrieved successfully',
    metadata: {
      timestamp: new Date().toISOString(),
      version: '1.0',
      type: 'alarms'
    },
    pagination: {
      total: filteredAlarms.length,
      page: 1,
      per_page: 50,
      total_pages: 1
    },
    data: filteredAlarms
  };
}

/**
 * Get a single alarm by ID
 */
export async function getAlarm(id: string): Promise<Alarm | null> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 300));
  
  const alarm = mockAlarms.find(alarm => alarm.id === id);
  return alarm || null;
}

/**
 * Acknowledge an alarm
 */
export async function acknowledgeAlarm(id: string, username: string): Promise<Alarm | null> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 300));
  
  const alarmIndex = mockAlarms.findIndex(alarm => alarm.id === id);
  if (alarmIndex === -1) return null;
  
  const alarm = { ...mockAlarms[alarmIndex] };
  alarm.status = 'acknowledged';
  alarm.acknowledgedBy = username;
  alarm.acknowledgedAt = new Date().toISOString();
  
  mockAlarms[alarmIndex] = alarm;
  return alarm;
}

/**
 * Resolve an alarm
 */
export async function resolveAlarm(id: string, username: string): Promise<Alarm | null> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 300));
  
  const alarmIndex = mockAlarms.findIndex(alarm => alarm.id === id);
  if (alarmIndex === -1) return null;
  
  const alarm = { ...mockAlarms[alarmIndex] };
  alarm.status = 'resolved';
  alarm.resolvedBy = username;
  alarm.resolvedAt = new Date().toISOString();
  
  mockAlarms[alarmIndex] = alarm;
  return alarm;
}
